import React from "react";
import { Card, Typography, Row, Col, Progress, Empty } from "antd";
import {
  EyeOutlined,
  SelectOutlined,
  DollarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";

const { Text, Title } = Typography;

const ConversionFunnel = ({
  title = "Conversion Funnel",
  views = 0,
  clicks = 0,
  conversions = 0,
  revenue = 0,
  loading = false,
  showRevenue = true,
  style = {},
}) => {
  // Calculate rates
  const ctr = views > 0 ? (clicks / views) * 100 : 0;
  const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
  const overallConversionRate = views > 0 ? (conversions / views) * 100 : 0;
  const avgOrderValue = conversions > 0 ? revenue / conversions : 0;

  // Funnel stages
  const funnelStages = [
    {
      label: "Views",
      value: views,
      icon: <EyeOutlined />,
      color: "#1890ff",
      backgroundColor: "#e6f7ff",
      rate: 100,
      rateLabel: null,
    },
    {
      label: "Clicks",
      value: clicks,
      icon: <SelectOutlined />,
      color: "#52c41a",
      backgroundColor: "#f6ffed",
      rate: ctr,
      rateLabel: "CTR",
    },
    {
      label: "Conversions",
      value: conversions,
      icon: <DollarOutlined />,
      color: "#722ed1",
      backgroundColor: "#f9f0ff",
      rate: conversionRate,
      rateLabel: "Conv. Rate",
    },
  ];

  if (views === 0 && clicks === 0 && conversions === 0) {
    return (
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Empty
          description="No funnel data available"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card title={title} bordered={false} loading={loading} style={style}>
        {/* Visual Funnel */}
        <Row gutter={16} justify="center" align="middle">
          {funnelStages.map((stage, index) => (
            <React.Fragment key={stage.label}>
              <Col>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ textAlign: "center" }}
                >
                  <div
                    style={{
                      width: 120 - index * 15,
                      height: 120 - index * 15,
                      borderRadius: "50%",
                      backgroundColor: stage.backgroundColor,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      border: `3px solid ${stage.color}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 24,
                        color: stage.color,
                        marginBottom: 4,
                      }}
                    >
                      {stage.icon}
                    </div>
                    <Text
                      strong
                      style={{ fontSize: 18, color: stage.color }}
                    >
                      {stage.value.toLocaleString()}
                    </Text>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Text strong style={{ display: "block" }}>
                      {stage.label}
                    </Text>
                    {stage.rateLabel && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {stage.rateLabel}: {stage.rate.toFixed(2)}%
                      </Text>
                    )}
                  </div>
                </motion.div>
              </Col>
              {index < funnelStages.length - 1 && (
                <Col>
                  <div style={{ padding: "0 20px", textAlign: "center" }}>
                    <ArrowRightOutlined
                      style={{ fontSize: 24, color: "#d9d9d9" }}
                    />
                    <div style={{ marginTop: 4 }}>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: "block",
                          color:
                            funnelStages[index + 1].rate < 5
                              ? "#ff4d4f"
                              : funnelStages[index + 1].rate < 20
                              ? "#faad14"
                              : "#52c41a",
                        }}
                      >
                        {funnelStages[index + 1].rate.toFixed(1)}%
                      </Text>
                    </div>
                  </div>
                </Col>
              )}
            </React.Fragment>
          ))}
        </Row>

        {/* Progress bars representation */}
        <div style={{ marginTop: 40 }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Views to Clicks (CTR)</Text>
              </div>
              <Progress
                percent={ctr}
                strokeColor={{
                  "0%": "#1890ff",
                  "100%": "#52c41a",
                }}
                format={() => `${ctr.toFixed(2)}%`}
                strokeWidth={12}
              />
            </Col>
            <Col span={24}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Clicks to Conversions</Text>
              </div>
              <Progress
                percent={conversionRate}
                strokeColor={{
                  "0%": "#52c41a",
                  "100%": "#722ed1",
                }}
                format={() => `${conversionRate.toFixed(2)}%`}
                strokeWidth={12}
              />
            </Col>
            <Col span={24}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Overall Conversion Rate</Text>
              </div>
              <Progress
                percent={overallConversionRate}
                strokeColor={{
                  "0%": "#1890ff",
                  "100%": "#722ed1",
                }}
                format={() => `${overallConversionRate.toFixed(2)}%`}
                strokeWidth={12}
              />
            </Col>
          </Row>
        </div>

        {/* Revenue Summary */}
        {showRevenue && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Card
                size="small"
                style={{ backgroundColor: "#f6ffed", textAlign: "center" }}
              >
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Total Revenue
                </Text>
                <Title level={4} style={{ margin: "8px 0 0", color: "#52c41a" }}>
                  ${revenue.toLocaleString()}
                </Title>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{ backgroundColor: "#f9f0ff", textAlign: "center" }}
              >
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Avg. Order Value
                </Text>
                <Title level={4} style={{ margin: "8px 0 0", color: "#722ed1" }}>
                  ${avgOrderValue.toFixed(2)}
                </Title>
              </Card>
            </Col>
          </Row>
        )}
      </Card>
    </motion.div>
  );
};

export default ConversionFunnel;
